/**
 * Password hashing seam. The core never imports node:crypto so that the in-process backend runs in the
 * browser; the self-hosted server injects a scrypt implementation (../node/scrypt-hasher.ts).
 */
export interface PasswordHasher {
  readonly id: string;
  hash(password: string): Promise<string>;
  verify(password: string, encoded: string): Promise<boolean>;
}

interface SubtleLike {
  importKey(format: 'raw', keyData: Uint8Array, algorithm: 'PBKDF2', extractable: false, usages: ['deriveBits']): Promise<unknown>;
  deriveBits(algorithm: { name: 'PBKDF2'; hash: 'SHA-256'; salt: Uint8Array; iterations: number }, key: unknown, length: number): Promise<ArrayBuffer>;
}

function subtle(): SubtleLike | null {
  const c = (globalThis as { crypto?: { subtle?: unknown } }).crypto;
  return (c?.subtle as SubtleLike | undefined) ?? null;
}

function b64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function unb64(text: string): Uint8Array {
  const s = atob(text);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

/**
 * PBKDF2-HMAC-SHA256 via Web Crypto (browsers, Node >= 19). Encoded as
 * `pbkdf2-sha256$<iterations>$<salt b64>$<hash b64>`. 600k iterations follows the OWASP 2023 guidance.
 */
export class WebCryptoPbkdf2Hasher implements PasswordHasher {
  readonly id = 'pbkdf2-sha256';

  constructor(
    private readonly iterations = 600_000,
    private readonly randomBytes: (n: number) => Uint8Array = (n) => {
      const out = new Uint8Array(n);
      (globalThis as { crypto: { getRandomValues(a: Uint8Array): Uint8Array } }).crypto.getRandomValues(out);
      return out;
    },
  ) {}

  static isAvailable(): boolean {
    return subtle() !== null;
  }

  private async derive(password: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
    const sub = subtle();
    if (!sub) throw new Error('Web Crypto subtle API is not available');
    const key = await sub.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits']);
    const bits = await sub.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, key, 256);
    return new Uint8Array(bits);
  }

  async hash(password: string): Promise<string> {
    const salt = this.randomBytes(16);
    const dk = await this.derive(password, salt, this.iterations);
    return `${this.id}$${this.iterations}$${b64(salt)}$${b64(dk)}`;
  }

  async verify(password: string, encoded: string): Promise<boolean> {
    const parts = encoded.split('$');
    if (parts.length !== 4 || parts[0] !== this.id) return false;
    const iterations = Number(parts[1]);
    if (!Number.isInteger(iterations) || iterations < 1 || iterations > 10_000_000) return false;
    const dk = await this.derive(password, unb64(parts[2]), iterations);
    return constantTimeEqual(dk, unb64(parts[3]));
  }
}
